import { makeObservable, observable, computed, action } from 'mobx'
import { MicCheck } from './MicCheck'
import { browserMicCheckDeps } from './browserDeps'
import type { MeetingCubit } from '../../MeetingCubit'
import { transcriptFailure, type TranscriptFailure } from './rules'
import type {
  MicReport,
  Problem,
  ProblemCause,
  ProblemLayer,
  ResolvedBy,
  SecretaryStatus,
  SecretaryStatusTrack,
  SoundcheckConfig,
} from './types'
import {
  NOOP_TELEMETRY,
  createSoundcheckTelemetry,
  type ProblemEvidence,
  type SoundcheckTelemetry,
} from './telemetry'

export type SoundcheckControllerDeps = {
  // Production convenience: the meeting supplies the settings (for config), the
  // passive logger (for telemetry with the room envelope) and, by default, the
  // browser MicCheck. Everything else is derived from it.
  meeting?: Pick<MeetingCubit, 'repository' | 'logEventPassive'>
  // Explicit overrides, used by tests and any host without a meeting. When a
  // meeting is not supplied, `config` is required.
  config?: () => SoundcheckConfig
  mic?: MicCheck
  telemetry?: SoundcheckTelemetry
  // Clock and timers default to the real ones; tests inject a fake clock.
  now?: () => number
  setTimer?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimer?: (handle: ReturnType<typeof setTimeout>) => void
}

type NewlyConfirmed = {
  layer: ProblemLayer
  cause: ProblemCause
  evidence: ProblemEvidence
}

// The worker's running total of speech starts on one mic in one run.
type SpeechStartsBaseline = { runId: string; trackSid: string; total: number }

/**
 * Answers two questions about the student and warns them when either answer
 * is no:
 *
 * 1. Is their microphone working? MicCheck decides; this keeps its failure as
 *    the microphone problem.
 * 2. Is their speech being transcribed? Checked only while the microphone
 *    works; see `transcriptFailure` in rules.ts.
 *
 * A microphone problem is shown before a transcript problem, because a broken
 * microphone already explains missing captions. This class owns the dialog and
 * banner state and records every decision in telemetry.
 */
export class SoundcheckController {
  readonly mic: MicCheck

  microphoneProblem: Problem | null = null
  transcriptProblem: Problem | null = null

  /** The one the UI shows. Recomputed on every evaluation. */
  problem: Problem | null = null

  private dialogOpen = false

  private readonly config: () => SoundcheckConfig
  private readonly now: () => number
  private readonly setTimer: (
    fn: () => void,
    ms: number
  ) => ReturnType<typeof setTimeout>

  private readonly clearTimer: (handle: ReturnType<typeof setTimeout>) => void
  private readonly telemetry: SoundcheckTelemetry
  private tickTimer: ReturnType<typeof setTimeout> | null = null
  private disposed = false

  // Microphone.
  private micReport: MicReport = { state: 'checking', reason: 'starting' }
  // When the mic last became ready; null while it is not ready.
  private micReadySince: number | null = null
  private localIdentity: string | null = null
  private currentTrackSid: string | null = null

  // Transcription. Previews and demos run without a transcriber.
  private transcriptionExpected = true
  // Which check opened the current transcript problem.
  private transcriptFailureKind: TranscriptFailure = null
  private lastCaptionAt: number | null = null
  // When a heartbeat last listed this mic; null until one does.
  private listedAt: number | null = null
  // When the current capture started: a new mic was published, or LiveKit
  // restarted capture under the same SID. The worker needs a moment to list it.
  private captureStartedAt: number | null = null
  private speechStartsSinceCaption = 0
  private speechStartsBaseline: SpeechStartsBaseline | null = null
  private heartbeatRunId: string | null = null
  private heartbeatSeq = -1

  // When LiveKit last named the student an active speaker. Telemetry only.
  private lastServerSpeakingAt: number | null = null

  // Notice / incident.
  private incidentStartedAt: number | null = null
  private dialogDismissed = false
  private lastNotificationsEnabled = false

  // Collected during a single evaluate() and flushed once priority is known.
  private newlyConfirmed: NewlyConfirmed[] = []

  constructor(deps: SoundcheckControllerDeps) {
    const { meeting } = deps

    this.now = deps.now ?? (() => Date.now())
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms))
    this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle))

    const config =
      deps.config ??
      (meeting ? () => meeting.repository.soundcheckSettings.settings : null)
    if (!config) {
      throw new Error('SoundcheckController requires a config or a meeting')
    }
    this.config = config

    // A real browser MicCheck by default; tests inject a fake.
    this.mic = deps.mic ?? new MicCheck(browserMicCheckDeps(config))

    // Telemetry through the meeting's passive logger so every row carries the
    // room envelope. Without a meeting (and no override) it stays a no-op.
    this.telemetry =
      deps.telemetry ??
      (meeting
        ? createSoundcheckTelemetry(meeting.logEventPassive, () => ({
            trackSid: this.currentTrackSid,
            micState: this.mic.report.state,
          }))
        : NOOP_TELEMETRY)

    makeObservable<
      SoundcheckController,
      'micReport' | 'dialogOpen' | 'setState'
    >(this, {
      microphoneProblem: observable.ref,
      transcriptProblem: observable.ref,
      problem: observable.ref,
      micReport: observable.ref,
      dialogOpen: observable,
      setState: action,
      bannerVisible: computed,
      dialogVisible: computed,
    })
  }

  initialize(): void {
    this.lastNotificationsEnabled = this.config().notificationsEnabled
    this.scheduleTick()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    if (this.tickTimer !== null) this.clearTimer(this.tickTimer)
    if (this.incidentStartedAt !== null) {
      this.telemetry.incidentEnded(
        'left',
        (this.now() - this.incidentStartedAt) / 1000,
        this.openCauses(),
        'meeting'
      )
    }
    this.mic.dispose()
  }

  get notificationsEnabled(): boolean {
    return this.config().notificationsEnabled
  }

  get bannerVisible(): boolean {
    return this.notificationsEnabled && this.problem !== null
  }

  get dialogVisible(): boolean {
    return this.notificationsEnabled && this.problem !== null && this.dialogOpen
  }

  // --- Inputs -------------------------------------------------------------

  reportMicReport(report: MicReport): void {
    if (this.disposed) return
    const now = this.now()
    if (report.state !== 'ready') this.micReadySince = null
    else if (this.micReadySince === null) this.micReadySince = now
    this.setState(() => {
      this.micReport = report
    })
    this.telemetry.micResult('meeting', report, null)
    this.evaluate(now)
  }

  reportTrackSid(sid: string | null): void {
    if (sid === this.currentTrackSid) return
    this.currentTrackSid = sid
    // What the worker heard, or did not, on the old mic says nothing about the
    // new one.
    this.listedAt = null
    this.captureStartedAt = sid === null ? null : this.now()
    this.speechStartsSinceCaption = 0
  }

  /**
   * LiveKit restarted capture under the same SID (a device change, or recovery
   * from an ended track), so reportTrackSid never fires, and MicCheck can stay
   * ready throughout. What the worker heard, or did not, on the old capture
   * says nothing about the new one. The worker keeps the same counter, and the
   * next heartbeat can still carry old-capture speech, so it is taken as a
   * baseline.
   */
  reportTrackRestarted(): void {
    this.captureStartedAt = this.now()
    this.speechStartsSinceCaption = 0
    this.speechStartsBaseline = null
  }

  setLocalIdentity(identity: string | null): void {
    this.localIdentity = identity
  }

  reportTranscriptionExpected(expected: boolean): void {
    if (this.disposed) return
    this.transcriptionExpected = expected
    this.evaluate(this.now())
  }

  /** An own caption, new or changed. Proof the student is being transcribed. */
  reportTranscript(): void {
    if (this.disposed) return
    const now = this.now()
    this.lastCaptionAt = now
    this.speechStartsSinceCaption = 0
    // The next heartbeat can still carry the speech starts that produced this
    // caption, so take it as a new baseline rather than counting them.
    this.speechStartsBaseline = null
    this.evaluate(now)
  }

  reportHeartbeat(status: SecretaryStatus): void {
    if (this.disposed) return
    // The data channel does not retry or order messages.
    const olderInSameRun =
      status.runId === this.heartbeatRunId && status.seq <= this.heartbeatSeq
    if (olderInSameRun) return
    this.heartbeatRunId = status.runId
    this.heartbeatSeq = status.seq

    const now = this.now()
    const row = status.tracks.find(
      (t) =>
        t.identity === this.localIdentity && t.trackId === this.currentTrackSid
    )
    if (row) {
      this.listedAt = now
      this.countSpeechStarts(status.runId, row)
    }
    this.evaluate(now)
  }

  /** Telemetry only: whether LiveKit hears the student. Decides nothing. */
  reportServerSpeaking(speaking: boolean): void {
    if (speaking) this.lastServerSpeakingAt = this.now()
  }

  // --- Notice actions -----------------------------------------------------

  dismissDialog(): void {
    if (this.disposed || !this.dialogOpen || this.problem === null) return
    const seconds =
      this.incidentStartedAt === null
        ? 0
        : (this.now() - this.incidentStartedAt) / 1000
    this.setState(() => {
      this.dialogOpen = false
    })
    this.dialogDismissed = true
    this.telemetry.dialogDismissed(this.problem.cause, seconds)
  }

  /** Record a requested reconnect's outcome. Does not itself close the incident. */
  reportRepairResult(
    cause: ProblemCause,
    republished: boolean,
    errorName: string,
    errorMessageSanitized: string
  ): void {
    this.telemetry.repairResult(
      cause,
      republished,
      errorName,
      errorMessageSanitized
    )
  }

  openDialog(): void {
    if (this.disposed || this.dialogOpen || this.problem === null) return
    this.setState(() => {
      this.dialogOpen = true
    })
    this.telemetry.dialogShown(this.problem.cause, 'banner')
  }

  // --- Evaluation ---------------------------------------------------------

  private evaluate(now: number): void {
    if (this.disposed) return
    this.newlyConfirmed = []
    this.updateMicrophoneProblem(now)
    this.updateTranscriptProblem(now)

    this.setState(() => {
      this.problem = this.microphoneProblem ?? this.transcriptProblem
    })

    this.flushProblemLogs(now)
    this.updateNotice(now)
  }

  private updateMicrophoneProblem(now: number): void {
    const r = this.micReport
    if (r.state === 'failed') {
      // A changed reason updates the details and keeps the incident.
      if (this.microphoneProblem?.cause === r.cause) return
      this.microphoneProblem = {
        cause: r.cause,
        since: this.microphoneProblem?.since ?? now,
      }
      this.newlyConfirmed.push({
        layer: 'microphone',
        cause: r.cause,
        evidence: {},
      })
    } else if (r.state === 'ready' && this.microphoneProblem !== null) {
      this.resolveLayer('microphone', this.microphoneProblem, 'mic_ready', now)
      this.microphoneProblem = null
    }
    // checking / inactive: keep any unresolved microphone problem as-is.
  }

  private updateTranscriptProblem(now: number): void {
    if (!this.transcriptionExpected) return
    // A caption proves transcription whatever the mic is doing now: one for
    // speech just before a mute still clears the problem.
    const problem = this.transcriptProblem
    if (problem !== null && atOrAfter(this.lastCaptionAt, problem.since)) {
      this.resolveTranscriptProblem(problem, 'caption', now)
      return
    }
    // Only a working microphone can be judged; a muted or failed one keeps any
    // open transcript problem as it is.
    const micReadySince = this.micReadySince
    if (micReadySince === null) return

    const config = this.config()
    // A new or restarted capture, or unmuting, gives the worker a full window.
    const listenedSince = Math.max(
      this.listedAt ?? micReadySince,
      this.captureStartedAt ?? micReadySince,
      micReadySince
    )
    const failure = transcriptFailure({
      now,
      lastCaptionAt: this.lastCaptionAt,
      listenedSince,
      speechStartsSinceCaption: this.speechStartsSinceCaption,
      windowMS: config.transcriptWindowMS,
      maxSpeechStartsWithoutCaption: config.maxSpeechStartsWithoutCaption,
    })

    if (failure !== null && problem === null) {
      this.transcriptProblem = { cause: 'no_transcript', since: now }
      this.transcriptFailureKind = failure
      this.newlyConfirmed.push({
        layer: 'transcript',
        cause: 'no_transcript',
        evidence:
          failure === 'not_listened'
            ? { notListenedMS: now - listenedSince }
            : { speechStartsSinceCaption: this.speechStartsSinceCaption },
      })
    } else if (failure === null && problem !== null) {
      // Without a caption, only a heartbeat listing the mic answers
      // not_listened. Anything else reset the evidence: a new or newly ready
      // mic, a restarted capture, or a restarted worker.
      const listedAgain =
        this.transcriptFailureKind === 'not_listened' &&
        atOrAfter(this.listedAt, problem.since)
      this.resolveTranscriptProblem(
        problem,
        listedAgain ? 'listening' : 'fresh_start',
        now
      )
    }
  }

  private resolveTranscriptProblem(
    problem: Problem,
    resolvedBy: ResolvedBy,
    now: number
  ): void {
    this.resolveLayer('transcript', problem, resolvedBy, now)
    this.transcriptProblem = null
    this.transcriptFailureKind = null
  }

  /**
   * Heartbeats carry the worker's running total; count the increase since the
   * previous one. A new worker run or a new mic restarts that total, so its
   * first value is a baseline, not new speech.
   */
  private countSpeechStarts(runId: string, row: SecretaryStatusTrack): void {
    if (row.speechStarts === undefined) return
    const baseline = this.speechStartsBaseline
    // A restarted worker is a fresh transcriber: what the old run failed to
    // caption says nothing about it.
    if (baseline !== null && baseline.runId !== runId) {
      this.speechStartsSinceCaption = 0
    }
    const sameCounter =
      baseline !== null &&
      baseline.runId === runId &&
      baseline.trackSid === row.trackId &&
      row.speechStarts >= baseline.total
    if (sameCounter) {
      this.speechStartsSinceCaption += row.speechStarts - baseline.total
    }
    this.speechStartsBaseline = {
      runId,
      trackSid: row.trackId,
      total: row.speechStarts,
    }
  }

  private resolveLayer(
    layer: ProblemLayer,
    problem: Problem,
    resolvedBy: ResolvedBy,
    now: number
  ): void {
    const secondsOpen = (now - problem.since) / 1000
    this.telemetry.resolved(
      layer,
      problem.cause,
      secondsOpen,
      resolvedBy,
      'meeting'
    )
  }

  private flushProblemLogs(now: number): void {
    const currentLayer = this.currentLayer()
    for (const nc of this.newlyConfirmed) {
      const shown = currentLayer === nc.layer
      this.telemetry.problem(
        nc.layer,
        nc.cause,
        shown,
        {
          ...nc.evidence,
          ownCaptionAgeMS: msSince(this.lastCaptionAt, now),
          serverSpeakingAgeMS: msSince(this.lastServerSpeakingAt, now),
        },
        'meeting'
      )
    }
    this.newlyConfirmed = []
  }

  private currentLayer(): ProblemLayer | null {
    if (this.problem === null) return null
    if (this.problem === this.microphoneProblem) return 'microphone'
    return 'transcript'
  }

  private updateNotice(now: number): void {
    const notificationsEnabled = this.notificationsEnabled
    const hasProblem = this.problem !== null

    // Incident lifecycle.
    if (hasProblem && this.incidentStartedAt === null) {
      this.incidentStartedAt = now
      this.dialogDismissed = false
      if (notificationsEnabled && this.problem) {
        this.setState(() => {
          this.dialogOpen = true
        })
        this.telemetry.dialogShown(this.problem.cause, 'auto')
      }
    } else if (!hasProblem && this.incidentStartedAt !== null) {
      const seconds = (now - this.incidentStartedAt) / 1000
      this.telemetry.incidentEnded('recovered', seconds, '', 'meeting')
      this.incidentStartedAt = null
      this.dialogDismissed = false
      this.setState(() => {
        this.dialogOpen = false
      })
    }

    // Notifications turned on mid-incident opens the current incident once.
    if (
      notificationsEnabled &&
      !this.lastNotificationsEnabled &&
      hasProblem &&
      !this.dialogOpen &&
      !this.dialogDismissed &&
      this.problem
    ) {
      this.setState(() => {
        this.dialogOpen = true
      })
      this.telemetry.dialogShown(this.problem.cause, 'notifications_enabled')
    }
    this.lastNotificationsEnabled = notificationsEnabled
  }

  private openCauses(): string {
    return [this.microphoneProblem, this.transcriptProblem]
      .filter((p): p is Problem => p !== null)
      .map((p) => p.cause)
      .join(',')
  }

  private setState(mutate: () => void): void {
    mutate()
  }

  private scheduleTick(): void {
    if (this.disposed) return
    this.tickTimer = this.setTimer(() => {
      this.tickTimer = null
      this.evaluate(this.now())
      this.scheduleTick()
    }, this.config().tickMS)
  }
}

function atOrAfter(at: number | null, since: number): boolean {
  return at !== null && at >= since
}

function msSince(at: number | null, now: number): number | null {
  return at === null ? null : now - at
}
